import { forwardRef, useContext } from 'react'
import { usePortfolioBalanceDisplay } from '../../hooks/usePortfolioBalanceDisplay'
import { PrivacyAmount } from '../../components/PrivacyAmount'
import { WalletContext } from '../../providers/wallet'
import { useTranslation } from '../../providers/language'

interface PortfolioHeroProps {
  collapseProgress?: number
}

/**
 * Fiat-primary total balance across BTC + all assets.
 * Matches master Balance component styling.
 */
const PortfolioHero = forwardRef<HTMLDivElement, PortfolioHeroProps>(function PortfolioHero(
  { collapseProgress = 0 },
  ref,
) {
  const { balance, maskedBalance, unit, secondaryBalance, secondaryUnit, maskedSecondary, showSecondary } =
    usePortfolioBalanceDisplay()
  const { syncing } = useContext(WalletContext)
  const { t } = useTranslation()
  const clampedProgress = Math.max(0, Math.min(1, collapseProgress))

  return (
    <div className='mb-2 mt-8 flex w-full flex-col items-center justify-center'>
      <div
        ref={ref}
        className='flex flex-col items-center'
        style={{
          opacity: 1 - clampedProgress,
          transform: `translate3d(0, ${-18 * clampedProgress}px, 0) scale(${1 - 0.28 * clampedProgress})`,
          transformOrigin: 'center top',
          willChange: clampedProgress > 0 && clampedProgress < 1 ? 'transform, opacity' : 'auto',
        }}
      >
        <div className='flex items-baseline gap-2'>
          <PrivacyAmount className='text-heading-xl' masked={maskedBalance} testId='main-balance' interactive>
            {balance}
          </PrivacyAmount>
          {unit ? <span className='text-xl'>{unit}</span> : null}
        </div>
        {showSecondary ? (
          <div className='mt-0.5 flex items-baseline gap-1 text-sm text-neutral-500'>
            <PrivacyAmount masked={maskedSecondary} testId='secondary-balance'>
              {secondaryBalance}
            </PrivacyAmount>
            {secondaryUnit ? <span>{secondaryUnit}</span> : null}
          </div>
        ) : null}
        {syncing ? (
          <p role='status' className='mt-1 text-sm text-neutral-500'>
            {t('wallet.syncing')}
          </p>
        ) : null}
      </div>
    </div>
  )
})

export default PortfolioHero
